import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List, 
  ListOrdered,
  Link as LinkIcon,
  Palette
} from 'lucide-react';

// 顏色選擇器
const ColorSelector = ({ isVisible, onToggle, editor }) => {
    const colors = [
        // 灰色系列
        { name: '灰-200', value: '#e5e7eb' },
        { name: '灰-400', value: '#9ca3af' },
        { name: '灰-600', value: '#4b5563' },
        { name: '灰-900', value: '#111827' },
        
        // 紅色系列
        { name: '紅-200', value: '#fecaca' },
        { name: '紅-400', value: '#f87171' },
        { name: '紅-600', value: '#dc2626' },
        { name: '紅-900', value: '#7f1d1d' },
        
        // 黃色系列
        { name: '黃-200', value: '#fde68a' },
        { name: '黃-400', value: '#fbbf24' },
        { name: '黃-600', value: '#d97706' },
        { name: '黃-900', value: '#78350f' },
        
        // 綠色系列
        { name: '綠-200', value: '#a7f3d0' },
        { name: '綠-400', value: '#34d399' },
        { name: '綠-600', value: '#059669' },
        { name: '綠-900', value: '#064e3b' },
        
        // 藍色系列
        { name: '藍-200', value: '#bfdbfe' },
        { name: '藍-400', value: '#60a5fa' },
        { name: '藍-600', value: '#2563eb' },
        { name: '藍-900', value: '#1e3a8a' },
        
        // 紫色系列
        { name: '紫-200', value: '#ddd6fe' },
        { name: '紫-400', value: '#a78bfa' },
        { name: '紫-600', value: '#7c3aed' },
        { name: '紫-900', value: '#4c1d95' },
        
        // 粉色系列
        { name: '粉-200', value: '#fbcfe8' },
        { name: '粉-400', value: '#f472b6' },
        { name: '粉-600', value: '#db2777' },
        { name: '粉-900', value: '#831843' }
      ];
      
  //   if (!isVisible) return null;

  const handleClick = (e, color) => {
    e.preventDefault(); // 防止表單提交
    e.stopPropagation(); // 防止事件冒泡
    editor.chain().focus(false).setColor(color).run();
    onToggle(false);
  };

  // 點擊外部關閉選擇器
  useEffect(() => {
    if (isVisible) {
      const handleClickOutside = (e) => {
        const target = e.target;
        if (!target.closest('.color-selector') && !target.closest('.color-button')) {
          onToggle(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, onToggle]);

  if (!isVisible) return null;

  return (
    <div className="absolute mt-2 p-2 bg-white rounded-lg shadow-lg left-0 top-full z-[100] color-selector w-36 h-40">
      <div className="grid grid-cols-4 gap-1 justify-items-center">
        {colors.map(({ name, value }) => (
          <button
            key={value}
            className="w-4 h-4 rounded-full transition-transform hover:scale-110"
            style={{ backgroundColor: value }}
            onClick={(e) => handleClick(e, value)}
            title={name}
            type="button" // 明確設置按鈕類型
          />
        ))}
      </div>
    </div>
  );
};

// 工具列按鈕元件
const MenuButton = ({ onClick, isActive, children, className }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded transition-colors duration-200 hover:bg-neutral-700/50
      ${isActive ? 'bg-neutral-700' : 'bg-transparent'} ${className || ''}`}
    type="button" // 明確設置按鈕類型
  >
    {children}
  </button>
);

// 編輯器
const TipTapEditor = forwardRef(({ value, onChange, disabled }, ref) => {
  const [showColorSelector, setShowColorSelector] = useState(false);
  const editorContainerRef = useRef(null);

  // 點擊連結按鈕的處理函數
  const handleLinkClick = (editor, e) => {
    e.preventDefault(); // 防止表單提交
    e.stopPropagation(); // 防止事件冒泡
    
    if (disabled) return;

    // 檢查是否有選取文字
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    
    // 取得目前的連結 URL（如果有的話）
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('輸入URL:', previousUrl || 'https://');

    if (url === null) {
      return;
    }

    // 如果清空 URL，移除連結
    if (url === '') {
      editor.chain().focus(false).unsetLink().run();
      return;
    }

    // 添加 https:// 如果沒有指定協議
    const validUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `https://${url}`;

    // 如果沒有選取文字，插入一個帶有連結的 HTML 內容
    if (!hasSelection) {
      editor
        .chain()
        .focus(false)
        .insertContent({
          type: 'text',
          marks: [{
            type: 'link',
            attrs: {
              href: validUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
              class: 'text-blue-600 underline hover:text-blue-800'
            }
          }],
          text: validUrl
        })
        .run();
    } else {
      // 如果有選取文字，直接設定連結
      editor
        .chain()
        .focus(false)
        .extendMarkRange('link')
        .setLink({ 
          href: validUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'text-blue-600 underline hover:text-blue-800'
        })
        .run();
    }
  };

  // 自訂連結
  const CustomLink = Link.configure({
    openOnClick: true, // 允許點擊開啟連結
    HTMLAttributes: {
      class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
      rel: 'noopener noreferrer',
      target: '_blank', // 在新分頁開啟
    },
    validate: href => true, // 允許所有 URL 格式
  });

  // 編輯器
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable the built-in link extension from StarterKit
        link: false,
      }),
      CustomLink,
      TextStyle,
      Underline,
      Color,
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm leading-normal max-w-none p-4 min-h-[150px] ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } focus:outline-none rounded-b-md`,
      },
      handleClick: (view, pos, event) => {
        // 確保點擊編輯器時不會失去焦點
        event.stopPropagation();
        return false; // 讓 Tiptap 處理剩餘的點擊事件
      },
    },
  });

  // 防止編輯器失去焦點
  useEffect(() => {
    if (editorContainerRef.current) {
      const container = editorContainerRef.current;
      
      const handleClick = (e) => {
        // 只處理來自編輯器內部的點擊
        if (container.contains(e.target) && editor) {
          e.stopPropagation(); // 阻止冒泡，防止觸發其他焦點事件
        }
      };
      
      container.addEventListener('mousedown', handleClick);
      
      return () => {
        container.removeEventListener('mousedown', handleClick);
      };
    }
  }, [editor]);

  // 暴露 editor 的 HTML 內容給父組件
  useImperativeHandle(ref, () => ({
    get innerHTML() {
      return editor?.getHTML() || '';
    },
    focus() {
      editor?.chain().focus(false).run();
    },
    clear() {
      editor?.commands.clearContent();
    }
  }));

  // 確保在 disabled 改變時更新可編輯狀態
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // 更新編輯器內容當 value 改變時
  useEffect(() => {
    if (editor && value && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  // 如果編輯器未初始化，顯示加載狀態
  if (!editor) {
    return <div className="border rounded border-gray-300 p-4 h-[150px] bg-gray-50 flex items-center justify-center">載入編輯器中...</div>;
  }

  // 統一處理文字格式按鈕的點擊
  const handleFormatClick = (e, command) => {
    e.preventDefault(); // 防止表單提交
    e.stopPropagation(); // 防止事件冒泡
    command();
  };

  return (
    <div className="w-full border rounded border-gray-300 overflow-hidden" ref={editorContainerRef}>
      <div className="flex items-center p-2 space-x-1 bg-neutral-800">
        <MenuButton 
          onClick={(e) => handleFormatClick(e, () => editor.chain().focus(false).toggleBold().run())}
          isActive={editor.isActive('bold')}
        >
          <Bold className="w-4 h-4 text-white" />
        </MenuButton>
        
        <MenuButton
          onClick={(e) => handleFormatClick(e, () => editor.chain().focus(false).toggleItalic().run())}
          isActive={editor.isActive('italic')}
        >
          <Italic className="w-4 h-4 text-white" />
        </MenuButton>
        
        <MenuButton
          onClick={(e) => handleFormatClick(e, () => editor.chain().focus(false).toggleUnderline().run())}
          isActive={editor.isActive('underline')}
        >
          <UnderlineIcon className="w-4 h-4 text-white" />
        </MenuButton>
        
        <div className="relative">
          <MenuButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (disabled) return;
              setShowColorSelector(!showColorSelector);
            }}
            className="color-button"
          >
            <Palette className="w-4 h-4 text-white" />
          </MenuButton>
          <ColorSelector 
            isVisible={showColorSelector} 
            onToggle={setShowColorSelector}
            editor={editor} 
          />
        </div>

        {/* <MenuButton
          onClick={(e) => handleFormatClick(e, () => editor.chain().focus(false).toggleBulletList().run())}
          isActive={editor.isActive('bulletList')}
        >
          <List className="w-4 h-4 text-white" />
        </MenuButton> */}

        {/* <MenuButton
          onClick={(e) => handleFormatClick(e, () => editor.chain().focus(false).toggleOrderedList().run())}
          isActive={editor.isActive('orderedList')}
        >
          <ListOrdered className="w-4 h-4 text-white" />
        </MenuButton> */}

        <MenuButton
          onClick={(e) => handleLinkClick(editor, e)}
          isActive={editor.isActive('link')}
        >
          <LinkIcon className="w-4 h-4 text-white" />
        </MenuButton>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
});

export default TipTapEditor;